import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Types for portfolio projects
interface Project {
  id: number;
  title: string;
  category: string[];
  description: string;
  technologies: string[];
  imageUrl: string;
  githubUrl?: string;
  projectUrl?: string;
  collaborators?: string[];
}

const PortfolioContainer = styled.div`
  padding: 8rem 2rem 6rem;
  min-height: calc(100vh - 14rem); /* Adjusted for navigation and footer */
  
  @media (max-width: 768px) {
    padding: 6rem 1rem 4rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    gap: 0.5rem;
  }
`;

const FilterButton = styled(motion.button)<{ isActive: boolean }>`
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  border: none;
  background-color: ${({ theme, isActive }) => 
    isActive ? theme.colors.primary : `${theme.colors.backgroundLight}cc`};
  color: ${({ theme, isActive }) => 
    isActive ? 'white' : theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: ${({ theme, isActive }) => 
      isActive ? theme.colors.primary : `${theme.colors.backgroundLight}ee`};
  }
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProjectCard = styled(motion.div)`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  @media (max-width: 768px) {
    max-width: 500px;
    margin: 0 auto;
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ProjectInfo = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ProjectCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ProjectCategory = styled.span`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TechTag = styled.span`
  background-color: ${({ theme }) => `${theme.colors.background}dd`};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ProjectLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  svg {
    margin-right: 0.5rem;
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

const CollaboratorsSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CollaboratorsTitle = styled.h4`
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CollaboratorsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CollaboratorTag = styled.span`
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.2rem;
  width: 100%;
  grid-column: 1 / -1;
`;

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5
    }
  },
  exit: { 
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.3
    }
  }
};

const PortfolioPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  
  // Fonction pour mapper les catégories de projets aux clés de traduction
  const getCategoryTranslationKey = (category: string): string => {
    switch (category) {
      case "Développement Web": return "webDev";
      case "IA & Systèmes distribués": return "aiSystems";
      case "Machine Learning": return "machineLearning";
      case "AI-Powered Apps": return "aiApps";
      case "Systèmes d'Information": return "informationSystems";
      case "DevOps": return "devOps";
      case "Virtualisation": return "virtualization";
      case "Computer Vision": return "computerVision";
      case "Extension Navigateur": return "browserExtension";
      case "Full-Stack": return "fullStack";
      case "WIP": return "wip";
      case "Tous": return "all";
      default: return category;
    }
  };
  
  // Fonction pour obtenir la traduction d'une catégorie
  const getTranslatedCategory = (category: string): string => {
    const key = getCategoryTranslationKey(category);
    return t(`portfolio:categories.${key}`);
  };
  
  // State for filtering projects
  const [filter, setFilter] = useState<string>(t('portfolio:categories.all'));
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  
  // Mettre à jour les projets lorsque la langue change
  useEffect(() => {
    // Sample data for portfolio projects
    const projects: Project[] = [
      {
        id: 1,
        title: t('portfolio:projects.1.title'),
        category: [t('portfolio:categories.webDev'), t('portfolio:categories.wip')],
        description: t('portfolio:projects.1.description'),
        technologies: t('portfolio:projects.1.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/site-maxime.jpg",
        githubUrl: "https://github.com/GRD-Maxime/maximegermond.com",
        projectUrl: "https://maximegermond.com"
      },
      {
        id: 2,
        title: t('portfolio:projects.2.title'),
        category: [t('portfolio:categories.aiSystems'), t('portfolio:categories.webDev'), t('portfolio:categories.wip')],
        description: t('portfolio:projects.2.description'),
        technologies: t('portfolio:projects.2.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/framework-ia.jpg",
        githubUrl: "",
        projectUrl: ""
      },
      {
        id: 3,
        title: t('portfolio:projects.3.title'),
        category: [t('portfolio:categories.machineLearning'), t('portfolio:categories.wip')],
        description: t('portfolio:projects.3.description'),
        technologies: t('portfolio:projects.3.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/ia-meteo-dattes.jpg",
        githubUrl: "",
        projectUrl: "",
        collaborators: ["Warda Ouali"]
      },
      {
        id: 4,
        title: t('portfolio:projects.4.title'),
        category: [t('portfolio:categories.informationSystems')],
        description: t('portfolio:projects.4.description'),
        technologies: t('portfolio:projects.4.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/lab-governance.jpg",
        githubUrl: "",
        projectUrl: ""
      },
      {
        id: 5,
        title: t('portfolio:projects.5.title'),
        category: [t('portfolio:categories.machineLearning'), t('portfolio:categories.computerVision'), t('portfolio:categories.aiApps')],
        description: t('portfolio:projects.5.description'),
        technologies: t('portfolio:projects.5.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/postural-ai.jpg",
        githubUrl: "https://github.com/Xenor33/KineData",
        projectUrl: "", // à compléter si démo en ligne
        collaborators: ["Warda Ouali",  "Roman Fechtchenko", "Ghassane Binahla", "Badr Lachkar", "Léa Pallard", "Karim Amri"]
      },
      {
        id: 6,
        title: t('portfolio:projects.6.title'),
        category: [t('portfolio:categories.machineLearning'), t('portfolio:categories.aiApps')],
        description: t('portfolio:projects.6.description'),
        technologies: t('portfolio:projects.6.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/ai-finance-tool.jpg",
        githubUrl: "https://github.com/GRD-Maxime/ai_finance_tool",
        projectUrl: "",
        collaborators: ["Warda Ouali"]
      },
      {
        id: 0,
        title: t('portfolio:projects.0.title'),
        category: [t('portfolio:categories.informationSystems'), t('portfolio:categories.devOps'), t('portfolio:categories.virtualization')],
        description: t('portfolio:projects.0.description'),
        technologies: t('portfolio:projects.0.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/homelab.jpg",
        githubUrl: "",
        projectUrl: "",
        collaborators: []
      },
      {
        id: 7,
        title: t('portfolio:projects.7.title'),
        category: [t('portfolio:categories.fullStack'), t('portfolio:categories.aiApps'), t('portfolio:categories.browserExtension'), t('portfolio:categories.aiSystems'), t('portfolio:categories.webDev')],
        description: t('portfolio:projects.7.description'),
        technologies: t('portfolio:projects.7.technologies', { returnObjects: true }) as string[],
        imageUrl: "/images/portfolio/jobtracker.jpg",
        githubUrl: "", // À compléter si le projet est rendu public
        projectUrl: ""
      },    
    ];
    
    setAllProjects(projects);
    
    // Mettre à jour le filtre lorsque la langue change
    setFilter(t('portfolio:categories.all'));
    
    // Mettre à jour les projets filtrés
    setFilteredProjects([...projects].sort((a, b) => a.id - b.id));
  }, [t, i18n.language]); // Recréer les projets lorsque la fonction t ou la langue change
  
  // Extract all unique categories, including those in arrays
  const extractCategories = () => {
    const uniqueCategories = new Set<string>();
    uniqueCategories.add(t('portfolio:categories.all'));
    
    allProjects.forEach(project => {
      if (Array.isArray(project.category)) {
        project.category.forEach(cat => uniqueCategories.add(cat));
      } else {
        uniqueCategories.add(project.category);
      }
    });
    
    return Array.from(uniqueCategories);
  };
  
  // Project categories
  const categories = extractCategories();
  
  // Filter projects by category
  const handleFilterChange = (category: string) => {
    setFilter(category);
    
    if (category === t('portfolio:categories.all')) {
      setFilteredProjects(allProjects.sort((a, b) => a.id - b.id));
    } else {
      setFilteredProjects(allProjects.filter(project => {
        if (Array.isArray(project.category)) {
          return project.category.includes(category);
        } else {
          return project.category === category;
        }
      }).sort((a, b) => a.id - b.id));
    }
  };
  
  return (
    <PortfolioContainer>
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t('portfolio:pageTitle')}
      </SectionTitle>
      
      <FilterContainer>
        {categories.map((category) => (
          <FilterButton
            key={category}
            isActive={filter === category}
            onClick={() => handleFilterChange(category)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {category}
          </FilterButton>
        ))}
      </FilterContainer>
      
      <ProjectsGrid>
        <AnimatePresence>
          {filteredProjects.length === 0 ? (
            <NoResults>
              {t('portfolio:labels.noResults')}
            </NoResults>
          ) : (
            filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                variants={fadeIn}
                initial="hidden"
                animate="visible"
                exit="exit"
                layout
              >
                <ProjectImage>
                  <img src={project.imageUrl} alt={project.title} />
                </ProjectImage>
                
                <ProjectInfo>
                  {Array.isArray(project.category) ? (
                    <ProjectCategories>
                      {project.category.map((cat, index) => (
                        <ProjectCategory key={index}>{cat}</ProjectCategory>
                      ))}
                    </ProjectCategories>
                  ) : (
                    <ProjectCategory>{project.category}</ProjectCategory>
                  )}
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  
                  <TechStack>
                    {project.technologies.map((tech, index) => (
                      <TechTag key={index}>{tech}</TechTag>
                    ))}
                  </TechStack>
                  
                  <ProjectLinks>
                    {project.projectUrl && (
                      <ProjectLink 
                        href={project.projectUrl} 
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21 13v10h-21v-19h12v2h-10v15h17v-8h2zm3-12h-10.988l4.035 4-6.977 7.07 2.828 2.828 6.977-7.07 4.125 4.172v-11z"/>
                        </svg>
                        {t('portfolio:buttons.visit')}
                      </ProjectLink>
                    )}
                    
                    {project.githubUrl && (
                      <ProjectLink 
                        href={project.githubUrl} 
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                        {t('portfolio:buttons.github')}
                      </ProjectLink>
                    )}
                  </ProjectLinks>

                  {project.collaborators && project.collaborators.length > 0 && (
                    <CollaboratorsSection>
                      <CollaboratorsTitle>{t('portfolio:labels.contributors')}</CollaboratorsTitle>
                      <CollaboratorsList>
                        {project.collaborators.map((collaborator, index) => (
                          <CollaboratorTag key={index}>{collaborator}</CollaboratorTag>
                        ))}
                      </CollaboratorsList>
                    </CollaboratorsSection>
                  )}
                </ProjectInfo>
              </ProjectCard>
            ))
          )}
        </AnimatePresence>
      </ProjectsGrid>
    </PortfolioContainer>
  );
};

export default PortfolioPage;